import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import apiClient from '../../../api/apiClient';
import cyclesAPI from '../../../api/cycles';
import { fetchWatchlist } from '../marketsSlice';
import { buildClockMarket, symbolKey } from './clockModel';
import { sideOf } from './clockSide';
import { getActiveAccountId, onAccountScopedClear } from '../../account/accountRegistry';

// One weekly-low (or, DWLF-552, weekly-high) window per watchlist symbol plus one screener read (DWLF-301). The Clock can pass its own
// list instead (DWLF-509): the same batch read, but never the per-symbol fallback below, since that list can
// be every tracked market.
// One batch windows read for the whole watchlist (DWLF-309). If the API answers without `results`
// (a deployment without the batch form), it falls back to one request per symbol.
// The last result per watchlist, shared by the Clock and the Desk's rail and kept briefly, so moving
// between symbols or pages does not refetch every window.
// Keyed by account, expiring, and dropped on sign-out with the account's stored state (charter
// invariant 3), so a cached read never crosses accounts.
const CACHE_MS = 5 * 60 * 1000;
const cache = new Map(); // `${accountId}|${side}|${'depth'|'plain'}|${symbolsKey}` → { at, state }
onAccountScopedClear(() => cache.clear());

/**
 * @param {{depth?: boolean, symbols?: string[]|null, side?: 'low'|'high'}} [opts]  depth: also read each window's depth band (DWLF-416)
 *   — the Clock shows it; the chart page's rail, which reads only the stage, does not ask for it. symbols: the
 *   markets to read instead of the watchlist (DWLF-509: a group or every tracked market); null while that
 *   list is still loading; omitted for the watchlist. side: the weekly lows (default) or highs (DWLF-552), with
 *   the screener's long or short rows.
 */
export default function useCycleClockData({ depth = false, symbols: chosen, side = 'low' } = {}) {
  const dispatch = useDispatch();
  const watchlist = useSelector((s) => s.markets.watchlist);
  const watchlistFetched = useSelector((s) => s.markets.watchlistLastFetched);
  const watchlistError = useSelector((s) => s.markets.watchlistError);
  const [state, setState] = useState({ loading: true, error: null, markets: [], verification: null });

  const ownList = chosen !== undefined;
  useEffect(() => { if (!ownList) dispatch(fetchWatchlist()); }, [dispatch, ownList]);

  const source = ownList ? (chosen || []) : (watchlist || []);
  const symbols = source.map((w) => (typeof w === 'string' ? w : w?.symbol)).filter(Boolean);
  const symbolsKey = symbols.join(',');

  // Nothing to fetch: an empty list, or a watchlist that failed to load. Say so rather than spin forever,
  // and drop markets left from a previous, longer list. A chosen list still loading (null) waits.
  useEffect(() => {
    if (symbolsKey) return;
    if (ownList) {
      setState(chosen
        ? { loading: false, error: null, empty: true, markets: [], verification: null }
        : { loading: true, error: null, markets: [], verification: null });
    } else if (watchlistError) setState({ loading: false, error: 'Could not load your watchlist.', markets: [], verification: null });
    else if (watchlistFetched) setState({ loading: false, error: null, empty: true, markets: [], verification: null });
  }, [symbolsKey, ownList, chosen, watchlistFetched, watchlistError]);

  useEffect(() => {
    if (!symbolsKey) return undefined;
    const cacheKey = `${getActiveAccountId() || 'anon'}|${side}|${depth ? 'depth' : 'plain'}|${symbolsKey}`;
    const hit = cache.get(cacheKey);
    if (hit && Date.now() - hit.at < CACHE_MS) {
      setState(hit.state);
      return undefined;
    }
    let cancelled = false;
    // The last read stays up while this one loads, unless it was the other side's (DWLF-552): a Lows → Highs
    // switch starts blank, never showing low-cycle positions or the lows' failure count under the high-side words.
    setState((s) => (s.side === side
      ? { ...s, loading: true, error: null, empty: false }
      : { loading: true, error: null, markets: [], verification: null }));

    const screener = apiClient.get('/screener/cycle-setups').then((r) => r.data).catch(() => null);
    const allFailed = (reason) => symbols.map(() => ({ status: 'rejected', reason }));
    const perSymbol = () => Promise.allSettled(
      symbols.map((symbol) => cyclesAPI.getWindows({ symbol, timeframe: '1w', side, depth })),
    );
    // Batch results mapped back to the per-symbol settled shape, in watchlist order.
    const windows = cyclesAPI.getWindowsBatch({ symbols, timeframe: '1w', side, depth })
      .then((batch) => {
        if (!Array.isArray(batch?.results)) return ownList ? allFailed(new Error('no batch form')) : perSymbol();
        const byRequested = new Map(batch.results.map((r) => [symbolKey(r.requested ?? r.symbol), r]));
        return symbols.map((symbol) => {
          const r = byRequested.get(symbolKey(symbol));
          if (!r || r.error) return { status: 'rejected', reason: new Error(r?.error || 'missing from batch') };
          return { status: 'fulfilled', value: { verification: batch.verification, low: r.low, high: r.high } };
        });
      })
      // A chosen list (a group, every tracked market) can be the whole universe: a failed batch is a failed
      // read, never one request per symbol.
      .catch((e) => (ownList ? allFailed(e) : perSymbol()));

    Promise.all([screener, windows]).then(([setups, results]) => {
      if (cancelled) return;
      const rows = new Map((setups?.[sideOf(side).screener] || []).map((row) => [symbolKey(row.symbol), row]));
      const now = Date.now();
      const markets = symbols.map((symbol, i) => {
        const res = results[i];
        return buildClockMarket(symbol, res.status === 'fulfilled' ? res.value : null, rows.get(symbolKey(symbol)), now, { failed: res.status === 'rejected', side });
      });
      const failed = results.filter((r) => r.status === 'rejected').length;
      const verification = results.find((r) => r.status === 'fulfilled' && r.value?.verification)?.value.verification || null;
      const next = {
        side,
        loading: false,
        error: failed === results.length ? 'Could not load cycle windows.' : null,
        partial: failed > 0 && failed < results.length ? failed : 0,
        screenerMissing: !setups,
        markets,
        verification,
      };
      if (!failed) cache.set(cacheKey, { at: Date.now(), state: next });
      setState(next);
    });
    return () => { cancelled = true; };
    // symbolsKey stands in for symbols
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolsKey, depth, side]);

  return state;
}

export const __clearCycleClockCache = () => cache.clear(); // tests
